// Login-page helpers for organizations that sign in with SSO (Okta).
// Spec: api/docs/superpowers/specs/2026-10-15-chart-prep-okta-sso-design.md.

export const REMEMBERED_PROVIDER_KEY = "agamon.sso.provider";
export const MAX_SSO_RESTARTS = 2;
export const SSO_ACCOUNT_ERROR = "This account isn't set up for this organization. Please contact Agamon support.";
export const SSO_RETRY_ERROR = "Sign-in didn't complete. Close this window and log in again from the extension.";

// Only the provider id is remembered, never the email: workstations are shared.
export function readRememberedProvider(storage) {
  try {
    return storage?.getItem(REMEMBERED_PROVIDER_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function rememberProvider(storage, provider) {
  try {
    storage?.setItem(REMEMBERED_PROVIDER_KEY, provider);
  } catch {
    // Storage blocked: the next login asks again. Nothing else depends on it.
  }
}

export function nextStepForLoginError(error) {
  const id = error?.id;
  if ((id === "sso_required" || id === "sso_reauth_required") && error.provider) {
    return { type: "oidc", provider: error.provider };
  }
  if (typeof id === "string" && id.startsWith("sso_")) return { type: "error", message: SSO_ACCOUNT_ERROR };
  return { type: "unknown" };
}

const RESTARTS_PARAM = "sso_restarts";

// Automatic Okta round trips are counted in the URL (it survives the redirect even when
// browser storage is blocked), so a misconfiguration ends in a message instead of a loop.
export function restartCount(searchParams) {
  const n = Number.parseInt(searchParams.get(RESTARTS_PARAM) ?? "", 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function withRestartCount(href, count) {
  const url = new URL(href);
  url.searchParams.set(RESTARTS_PARAM, String(count));
  return url.toString();
}

// prompt=login ("switch account") marks every load of that login challenge with forceReauth.
// Honour it once: the forced Okta start comes back with sso_restarts=1, and from then on an
// active session is accepted, otherwise logout -> Okta -> back -> logout would never end.
export function shouldForceLogout(realm, searchParams) {
  return realm?.forceReauth === true && restartCount(searchParams) === 0;
}

// A `flow` param means Kratos sent the user back here (e.g. Okta refused them); show its
// message instead of bouncing to Okta again.
export function shouldAutoStartSso(realm, searchParams) {
  return realm?.method === "oidc" && !searchParams.get("flow");
}
